import { useEffect, useState } from "react";
import {
  Plus,
  X,
  Edit3,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  Warehouse as WarehouseIcon,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function WarehousePage({ setCurrentPage }) {
  const [warehouses, setWarehouses] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  const [wareHouseName, setWareHouseName] = useState("");
  const [wareHCode, setWareHCode] = useState("");
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 8;
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // ================= API =================

  const fetchWarehouses = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/WareHouse`);
      if (!res.ok) throw new Error("Failed to fetch warehouses");

      const data = await res.json();

      const mapped = data.map((w) => ({
        id: w.id,
        wareHouseName: w.wareHouseName || w.WareHouseName,
        wareHCode: w.wareHCode || w.WareHCode,
      }));

      setWarehouses(mapped);
      setFiltered(mapped);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load warehouses", "error");
    }
  };

  const createWarehouse = async () => {
    if (!wareHouseName || !wareHCode) {
      Swal.fire("Oops!", "All fields are required", "warning");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}api/WareHouse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          WareHouseName: wareHouseName,
          WareHCode: wareHCode,
        }),
      });

      if (!res.ok) throw new Error("Create failed");

      closeModal();
      fetchWarehouses();
      Swal.fire("Success", "Warehouse created", "success");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const updateWarehouse = async () => {
    if (!wareHouseName || !wareHCode) {
      Swal.fire("Oops!", "All fields are required", "warning");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}api/WareHouse/${selectedWarehouse.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Id: selectedWarehouse.id,
            WareHouseName: wareHouseName,
            WareHCode: wareHCode,
          }),
        }
      );

      if (!res.ok) throw new Error("Update failed");

      closeModal();
      fetchWarehouses();
      Swal.fire("Updated", "Warehouse updated", "success");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const deleteWarehouse = async (w) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Delete ${w.wareHouseName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}api/WareHouse/${w.id}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error("Delete failed");

      fetchWarehouses();
      Swal.fire("Deleted", "Warehouse removed", "success");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  // ================= EFFECTS =================

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(warehouses);
    } else {
      const term = search.toLowerCase();
      setFiltered(
        warehouses.filter(
          (w) =>
            w.wareHouseName.toLowerCase().includes(term) ||
            w.wareHCode.toLowerCase().includes(term)
        )
      );
    }
    setPage(1);
  }, [search, warehouses]);

  // ================= HELPERS =================

  const openEditModal = (w) => {
    setSelectedWarehouse(w);
    setWareHouseName(w.wareHouseName);
    setWareHCode(w.wareHCode);
    setOpenEdit(true);
  };

  const closeModal = () => {
    setOpenCreate(false);
    setOpenEdit(false);
    setSelectedWarehouse(null);
    setWareHouseName("");
    setWareHCode("");
  };

  // ================= UI =================

  return (
    <div className="p-8">
      <button
        onClick={() => setCurrentPage("referencess")}
        className="mb-4 flex items-center gap-2 text-indigo-600 font-semibold"
      >
        <ChevronLeft size={18} /> Back
      </button>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl font-bold flex items-center gap-3">
          <WarehouseIcon size={36} className="text-indigo-600" />
          Warehouses
        </h1>
        <button
          onClick={() => setOpenCreate(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl shadow-lg"
        >
          <Plus size={20} /> Create Warehouse
        </button>
      </div>

      {/* SEARCH */}
      <div className="relative max-w-md mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="w-full pl-12 pr-4 py-3 border-2 rounded-xl"
          placeholder="Search warehouse..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
            <tr>
              <th className="px-6 py-4 text-left">ID</th>
              <th className="px-6 py-4 text-left">Code</th>
              <th className="px-6 py-4 text-left">Name</th>
              <th className="px-6 py-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((w) => (
              <tr key={w.id} className="hover:bg-indigo-50">
                <td className="px-6 py-4">{w.id}</td>
                <td className="px-6 py-4">{w.wareHCode}</td>
                <td className="px-6 py-4 font-semibold">
                  {w.wareHouseName}
                </td>
                <td className="px-6 py-4 flex justify-center gap-2">
                  <button
                    onClick={() => openEditModal(w)}
                    className="p-2 bg-blue-500 text-white rounded-lg"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => deleteWarehouse(w)}
                    className="p-2 bg-red-500 text-white rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* PAGINATION */}
        <div className="flex justify-between p-4 bg-gray-50">
          <span>
            Page {page} of {totalPages || 1}
          </span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft />
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {(openCreate || openEdit) && (
        <Modal title={openCreate ? "Create Warehouse" : "Edit Warehouse"} close={closeModal}>
          <Input label="Warehouse Code" value={wareHCode} onChange={setWareHCode} />
          <Input label="Warehouse Name" value={wareHouseName} onChange={setWareHouseName} />

          <button
            onClick={openCreate ? createWarehouse : updateWarehouse}
            className="w-full mt-6 bg-indigo-600 text-white py-3 rounded-xl"
          >
            {openCreate ? "Create" : "Update"}
          </button>
        </Modal>
      )}
    </div>
  );
}

// ================= COMPONENTS =================

function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-2xl w-full max-w-md relative">
        <button onClick={close} className="absolute top-4 right-4">
          <X />
        </button>
        <h2 className="text-2xl font-bold mb-6">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="block mb-1 font-semibold">{label}</label>
      <input
        className="w-full border-2 rounded-lg px-4 py-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
